import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { Flex } from '../../../components/Flex'
import { createTFunction, getCompanyCountry } from '../../../utils/helpers'
import { Box } from '../../../components/Box'
import { CollapseContent } from '../../../components/Collapse'
import { Space } from '../../../components/Space'
import { Text } from '../../../components/Text'
import { Button } from '../../../components/Button'
import { themeColor } from '../../../components/theme'
import { useApiData } from '../../../hooks/useApiData'
import { getOfficialDocumentsCountries } from '../../../redux/config'
import { DomainsActions, DomainType } from '../../../redux/domains'
import { TaxesFootnote } from '../../TaxesFootnote'
import { CompanyDetailActions, isCompanyLoading } from '../../../redux/companyDetail'
import { RootState } from '../../../redux/types'
import { AntLabeledValue, Select } from '../../../components/Select'
import ReportSectionHeader from '../../../components/ReportSectionHeader'
import { useConsentData } from '../../../hooks/useConsentData'

import { ReportComponent, ReportUmbracoData } from './Report'
import { ArrowIcon } from './styled'
import { CompanyDetailReportsProps, Report, ReportType } from './types'
import { DocumentsInfo } from './DocumentsInfo'

const FilterContainer = styled(Flex)`
  width: 100%;

  border-radius: 4px;
  background: ${themeColor('primary')};
`

const StyledSelect = styled(Select)`
  align-self: center;
  max-width: 100%;
  width: 300px;
`

const ALL_REPORTS_FILTER = {
  value: 'all',
  label: 'All reports'
}

type ReportDetailRes = {
  data: ReportUmbracoData[]
}

type GroupedReport = {
  parent: [Report, ReportUmbracoData | undefined]
  child: [Report | undefined, ReportUmbracoData | undefined]
}

function createUmbracoDataMap(reportsUmbracoData?: ReportUmbracoData[]): Record<string, ReportUmbracoData> {
  return _.reduce(
    reportsUmbracoData,
    (acc, umbracoData) => ({
      ...acc,
      [umbracoData.bifCode]: umbracoData
    }),
    {}
  )
}

function groupLinkedProducts(availableReports: Report[], products: ReportUmbracoData[]) {
  const usedBifCodes: string[] = []

  return _.reduce(
    availableReports,
    (acc, ar) => {
      if (_.includes(usedBifCodes, ar.code) || _.some(products, x => x.childProductBifCode == ar.code)) return acc

      usedBifCodes.push(ar.code)

      const umbracoParent = _.find(products, p => p.bifCode === ar.code)
      let umbracoChild = undefined
      let reportChild = undefined

      if (umbracoParent?.childProductBifCode) {
        umbracoChild = _.find(products, p => p.bifCode === umbracoParent?.childProductBifCode)
        reportChild = _.find(availableReports, r => r.code === umbracoParent?.childProductBifCode)

        usedBifCodes.push(umbracoParent?.childProductBifCode)
      }

      let grp: GroupedReport = { parent: [ar, umbracoParent], child: [reportChild, umbracoChild] }

      // Try to put online product as first if exists
      if (grp.parent[0].type === ReportType.Offline && grp.child[0]) {
        grp = { parent: [grp.child[0], grp.child[1]], child: grp.parent }
      }

      return [...acc, grp]
    },
    [] as GroupedReport[]
  )
}

export const Reports = ({
  reports,
  companyIdentifier,
  companyDetail,
  renderDeliveryOptionLabel,
  officialDocumentsConfig,
  preferredProductsData,
  refreshPreferredProductsData,
  loadingPreferredProducts,
  showPriceForAnonymousUsers = true
}: CompanyDetailReportsProps) => {
  const { t, i18n } = useTranslation(['companyDetail', 'core'])
  const tc = createTFunction('core:', t)
  const tcd = createTFunction('companyDetail:', t)

  const dispatch = useDispatch()
  const { language } = i18n

  const officialDocumentsCountries = useSelector(getOfficialDocumentsCountries)
  const companyLoading = useSelector((state: RootState) => isCompanyLoading(state, { companyIdentifier }))
  const countryCode = getCompanyCountry(companyIdentifier)
  const viewOfficialDocuments = _.includes(officialDocumentsCountries, countryCode)
  const displayDocumentsPromo = viewOfficialDocuments && companyDetail?.areDocumentsInPriceList === false
  const documentsLimitReached = companyDetail?.documentsLimitReached === true

  const company = companyDetail?.company
  const officialDocumentsRequested = companyDetail?.officialDocumentsRequested
  const formerName = company?.name

  const [selectedDocument, setSelectedDocument] = useState<string | undefined>(companyDetail?.selectedDocumentCode)
  const [initialSortDone, setInitialSortDone] = useState<boolean>(false)

  useEffect(() => {
    if (!companyLoading) {
      setInitialSortDone(false)
    }
  }, [companyLoading])

  useEffect(() => {
    dispatch(
      DomainsActions.loadDomains({
        domainTypes: [DomainType.DeliveryOption, DomainType.Culture],
        language
      })
    )
  }, [dispatch, language])

  const [filterValue] = useState<React.ReactText>(ALL_REPORTS_FILTER.value)

  const reportCodes = reports?.map(r => r.code) ?? []
  const documentCodes = companyDetail?.documentsKeyValues?.map(d => d.key) ?? []
  const allProductCodes = _.uniq(reportCodes.concat(documentCodes))

  const { data: reportsUmbracoData, loading: reportsDetailLoading } = useApiData<ReportDetailRes>(
    {
      url: '/api/products/products',
      method: 'post',
      data: { productCodes: allProductCodes }
    },
    {
      umbracoCall: true,
      skip: _.isEmpty(allProductCodes)
    }
  )

  const umbracoReportsMap = createUmbracoDataMap(reportsUmbracoData?.data)

  const { data: consentDialogData, loading } = useConsentData()

  const filteredReports = useMemo(
    () => (filterValue === ALL_REPORTS_FILTER.value ? reports : _.filter(reports, x => x.type === filterValue)),
    [filterValue, reports]
  )

  const filteredStandardReports = useMemo(
    () => _.filter(filteredReports, x => _.isEmpty(x.documentDates)),
    [filteredReports]
  )
  const filteredDocuments = _.filter(filteredReports, x => !_.isEmpty(x.documentDates))

  const reportLoading = reportsDetailLoading || loading || (loadingPreferredProducts && !preferredProductsData)

  const ReportSectionSpace: React.FC = ({ children }) => (
    <Space direction="vertical" width="100%" flexDirection="column" space={2}>
      {children}
    </Space>
  )

  const [groupedNormalReports, setGroupNormalReports] = useState<GroupedReport[] | undefined>()
  const [umbracoDataApplied, setUmbracoDataApplied] = useState<boolean>(false)
  useEffect(() => {
    setGroupNormalReports(groupLinkedProducts(filteredStandardReports, reportsUmbracoData?.data || []))
    if (reportsUmbracoData?.data) {
      setUmbracoDataApplied(true)
    }
  }, [reportsUmbracoData, filteredStandardReports])

  const preferredProducts = preferredProductsData?.preferredProducts
  const preferredProductCodes = useMemo(
    () =>
      _.map(
        _.filter(preferredProducts, pp => pp.countryCode === countryCode),
        pp => pp.productCode
      ),
    [preferredProducts, countryCode]
  )

  const preferredGroupProduct = _.find(groupedNormalReports, gnr =>
    _.some(preferredProductCodes, pp =>
      _.includes(_.compact([gnr.parent[0].code, gnr.child && gnr.child[0] ? gnr.child[0].code : undefined]), pp)
    )
  )

  const preferredParentProduct =
    !!preferredGroupProduct?.parent[0].code && _.includes(preferredProductCodes, preferredGroupProduct?.parent[0].code)
      ? preferredGroupProduct.parent[0]
      : undefined

  const preferredChildProduct =
    preferredGroupProduct?.child &&
    !!preferredGroupProduct?.child[0]?.code &&
    _.includes(preferredProductCodes, preferredGroupProduct?.child[0].code)
      ? preferredGroupProduct.child[0]
      : undefined

  useEffect(() => {
    if (groupedNormalReports && umbracoDataApplied && !loadingPreferredProducts && !initialSortDone) {
      setInitialSortDone(true)
      if (preferredGroupProduct) {
        setGroupNormalReports(
          _.concat(
            [preferredGroupProduct],
            _.filter(groupedNormalReports, gnr => gnr !== preferredGroupProduct)
          )
        )
      }
    }
  }, [groupedNormalReports, initialSortDone, loadingPreferredProducts, preferredGroupProduct, umbracoDataApplied])

  return (
    <ReportSectionSpace>
      <FilterContainer justifyContent="space-between">
        <ReportSectionHeader>{t('AvailableReports')}</ReportSectionHeader>
      </FilterContainer>
      {_.map(groupedNormalReports, (grp, index) => {
        const {
          parent: [report, umbracoProduct],
          child: [childReport, childUmbracoProduct]
        } = grp

        return (
          <ReportComponent
            key={report.code}
            report={report}
            umbracoData={umbracoProduct}
            childReport={childReport}
            childUmbracoData={childUmbracoProduct}
            loading={reportLoading}
            company={company}
            requireConsent={report.consentRequired}
            renderDeliveryOptionLabel={renderDeliveryOptionLabel}
            consentData={consentDialogData}
            index={index + 1}
            showPriceForAnonymousUser={showPriceForAnonymousUsers}
            isParentPreferred={!!report && report === preferredParentProduct}
            isChildPreferred={!!childReport && childReport === preferredChildProduct}
            isAnotherProductPreferred={!!preferredGroupProduct && preferredGroupProduct !== grp}
            refreshPreferred={refreshPreferredProductsData}
            loadingPreferred={loadingPreferredProducts}
          />
        )
      })}
      <TaxesFootnote />

      {viewOfficialDocuments && (
        <CollapseContent
          defaultOpen={true}
          renderHeader={open => (
            <FilterContainer justifyContent="space-between" alignItems="center">
              <Flex alignItems="center">
                <ReportSectionHeader>{t('AvailableDocuments')}</ReportSectionHeader>
                {officialDocumentsRequested && !displayDocumentsPromo && (
                  <ReportSectionHeader>
                    <StyledSelect
                      ml={2}
                      placeholder={tcd('ChooseDocumentType')}
                      allowClear
                      showSearch
                      filterOption={(input: string, option?: any) =>
                        _.includes(_.toLower(option?.children), _.toLower(input))
                      }
                      options={_.map(companyDetail.documentsKeyValues, dkv => ({
                        label: umbracoReportsMap[dkv.key]?.name || dkv.value,
                        value: dkv.key
                      }))}
                      loading={companyLoading}
                      onChange={(
                        value: React.ReactText | AntLabeledValue | React.ReactText[] | AntLabeledValue[] | undefined
                      ) => {
                        const valueString = _.toString(value)
                        setSelectedDocument(valueString)
                        dispatch(
                          CompanyDetailActions.loadCompanyWithSelectedDocument({
                            companyIdentifier,
                            documentCode: valueString,
                            formerName
                          })
                        )
                      }}
                      onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                        event.stopPropagation()
                      }}
                      value={selectedDocument}
                      showOptionsOnLoading={true}
                    />
                  </ReportSectionHeader>
                )}
              </Flex>
              <Box p={2}>
                <ArrowIcon opened={open} type="arrowRightWhite" />
              </Box>
            </FilterContainer>
          )}
        >
          {displayDocumentsPromo || !officialDocumentsRequested ? (
            <DocumentsInfo
              companyIdentifier={companyIdentifier}
              documentsInfoData={officialDocumentsConfig}
              mode={displayDocumentsPromo ? 'Promo' : 'TriggerLoad'}
            />
          ) : filteredDocuments.length > 0 ? (
            <ReportSectionSpace>
              {_.map(filteredDocuments, (document, index) => {
                return (
                  <ReportComponent
                    key={document.code}
                    report={document}
                    umbracoData={umbracoReportsMap[document.code]}
                    loading={reportsDetailLoading || loading}
                    company={company}
                    requireConsent={document.consentRequired}
                    renderDeliveryOptionLabel={renderDeliveryOptionLabel}
                    consentData={consentDialogData}
                    index={index + 1}
                    showPriceForAnonymousUser={showPriceForAnonymousUsers}
                    isParentPreferred={false}
                    isChildPreferred={false}
                    isAnotherProductPreferred={!!preferredGroupProduct}
                    refreshPreferred={refreshPreferredProductsData}
                    loadingPreferred={loadingPreferredProducts}
                  />
                )
              })}
              {companyDetail.moreDocumentsAvailable && !companyDetail.selectedDocumentCode && (
                <Flex justifyContent="center" alignItems="center">
                  <Button
                    variant="secondary"
                    htmlType="submit"
                    loading={companyLoading}
                    disabled={companyLoading}
                    onClick={() =>
                      dispatch(CompanyDetailActions.loadCompanyWithMoreDocuments({ companyIdentifier, formerName }))
                    }
                  >
                    {tc('LoadMore')}
                  </Button>
                </Flex>
              )}
              <TaxesFootnote />
            </ReportSectionSpace>
          ) : documentsLimitReached ? (
            <Text>{t('DocumentsLimitReached')}</Text>
          ) : (
            <Text>{t('NoDocumentsAvailable')}</Text>
          )}
        </CollapseContent>
      )}
    </ReportSectionSpace>
  )
}
